import { useEffect, useState } from "react";
import { Check, Loader2, Pencil, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtRp } from "@/lib/format";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { KpiCard } from "@/components/common/Cards";
import { TYPE_LABEL, cardName } from "@/components/cc/ccUtils";

export function StatementDialog({ statement, onClose, onChanged }) {
  const [s, setS] = useState(statement);
  const [edit, setEdit] = useState(null); // {id, vendor, category}
  const [saving, setSaving] = useState(false);
  const [label, setLabel] = useState(statement?.card_label || "");

  useEffect(() => {
    setS(statement);
    setLabel(statement?.card_label || "");
  }, [statement]);

  if (!s) return null;

  const saveTx = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/cc/statements/${s.id}/transactions/${edit.id}`, { vendor: edit.vendor, category: edit.category });
      setS(data);
      setEdit(null);
      onChanged && onChanged();
    } catch (e) {
      toast.error(errMsg(e, "Gagal menyimpan"));
    } finally {
      setSaving(false);
    }
  };
  const saveLabel = async () => {
    try {
      const { data } = await api.patch(`/cc/statements/${s.id}`, { card_label: label });
      setS(data);
      toast.success("Nama kartu disimpan");
      onChanged && onChanged();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <Dialog open={!!statement} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl bg-card border-border max-h-[90vh] overflow-y-auto" data-testid="cc-statement-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{cardName(s)}</DialogTitle>
          <DialogDescription>
            {[s.bank, s.period && `Periode ${s.period}`, s.statement_date && `Cetak ${s.statement_date}`, s.due_date && `Jatuh tempo ${s.due_date}`].filter(Boolean).join(" · ")}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-[220px]">
            <div className="text-[11px] text-muted-foreground mb-1">Nama kartu</div>
            <Input value={label} onChange={(e) => setLabel(e.target.value)} className="h-9 bg-[hsl(220_12%_8%)]" data-testid="cc-statement-label-input" />
          </div>
          <Button variant="outline" onClick={saveLabel} className="h-9" data-testid="cc-statement-label-save">Simpan nama</Button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiCard label="Total tagihan" value={fmtRp(s.total_due)} accent testid="cc-dlg-total-due" />
          <KpiCard label="Total transaksi" value={fmtRp(s.total_charges)} testid="cc-dlg-charges" />
          <KpiCard label="Kredit / refund" value={fmtRp(s.total_credits)} testid="cc-dlg-credits" />
          <KpiCard label="Jumlah baris" value={(s.transactions || []).length} sub={`Token AI ${s.ai_tokens || 0}`} testid="cc-dlg-count" />
        </div>

        {s.summary && (
          <div className="rounded-xl border border-[color:var(--hairline)] bg-[hsl(43_30%_7%)] p-4" data-testid="cc-dlg-summary">
            <div className="eyebrow flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5" /> Rangkuman AI</div>
            <p className="text-sm text-[hsl(45_20%_86%)] mt-2 leading-relaxed">{s.summary}</p>
          </div>
        )}

        <div className="rounded-xl border border-border overflow-x-auto">
          <table className="w-full text-sm" data-testid="cc-dlg-tx-table">
            <thead>
              <tr className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground border-b border-border">
                <th className="text-left px-3 py-2.5 font-medium">Tanggal</th>
                <th className="text-left px-3 py-2.5 font-medium">Deskripsi</th>
                <th className="text-left px-3 py-2.5 font-medium">Vendor</th>
                <th className="text-left px-3 py-2.5 font-medium">Kategori</th>
                <th className="text-left px-3 py-2.5 font-medium">Jenis</th>
                <th className="text-right px-3 py-2.5 font-medium">Jumlah</th>
                <th className="px-2 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {(s.transactions || []).map((t) => {
                const editing = edit?.id === t.id;
                return (
                  <tr key={t.id} className="border-b border-border/60 last:border-0 hover:bg-[hsl(220_10%_9%)]" data-testid={`cc-dlg-tx-${t.id}`}>
                    <td className="px-3 py-2 num text-xs whitespace-nowrap text-muted-foreground">{t.date || "–"}</td>
                    <td className="px-3 py-2 text-xs max-w-[260px]">
                      <div className="truncate" title={t.description}>{t.description}</div>
                      {t.orig_currency && t.orig_amount > 0 && <div className="text-[10px] text-muted-foreground num">{t.orig_currency} {t.orig_amount}</div>}
                    </td>
                    <td className="px-3 py-2">
                      {editing ? (
                        <Input value={edit.vendor} onChange={(e) => setEdit({ ...edit, vendor: e.target.value })} className="h-8 text-xs" data-testid="cc-dlg-edit-vendor" />
                      ) : (
                        <span className="text-xs">{t.vendor}</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {editing ? (
                        <Input value={edit.category} onChange={(e) => setEdit({ ...edit, category: e.target.value })} className="h-8 text-xs" data-testid="cc-dlg-edit-category" />
                      ) : (
                        <Badge variant="outline" className="border-[color:var(--hairline)] text-[10px] text-[hsl(43_60%_66%)]">{t.category}</Badge>
                      )}
                    </td>
                    <td className="px-3 py-2 text-[11px] text-muted-foreground whitespace-nowrap">
                      {TYPE_LABEL[t.type] || t.type}
                      {t.report_number && <div className="text-[10px] text-primary">{t.report_number}</div>}
                    </td>
                    <td className={`px-3 py-2 text-right num whitespace-nowrap ${t.amount < 0 ? "text-[hsl(160_55%_56%)]" : ""}`}>{fmtRp(t.amount)}</td>
                    <td className="px-2 py-2 whitespace-nowrap">
                      {editing ? (
                        <div className="flex gap-1">
                          <Button size="icon" className="h-7 w-7" onClick={saveTx} disabled={saving} data-testid="cc-dlg-edit-save" aria-label="Simpan">
                            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                          </Button>
                          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEdit(null)} aria-label="Batal"><X className="h-3.5 w-3.5" /></Button>
                        </div>
                      ) : (
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEdit({ id: t.id, vendor: t.vendor, category: t.category })} data-testid={`cc-dlg-edit-${t.id}`} aria-label="Ubah">
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
}
